/**
 * Popup de newsletter de MineraliA.
 *
 * Reglas:
 * - Espera a que el visitante decida sobre cookies (`mineralia:consent-decided`).
 * - Si el banner nativo de Shopify (#shopify-pc__banner) está visible, espera a `visitorConsentCollected`.
 * - Después se abre a los N segundos o al recorrer X % de la página, lo que ocurra primero.
 * - Al cerrarlo se oculta durante N días; tras suscribirse no vuelve a mostrarse.
 * - Si el formulario regresa con éxito o error (#MineraliaNewsletterPopup), se abre con el mensaje.
 * - En el editor de temas sólo se muestra cuando la sección está seleccionada.
 */

const STORAGE_KEY = 'mineralia-newsletter-popup';
const CONSENT_EVENT = 'mineralia:consent-decided';
const FORM_HASH = '#MineraliaNewsletterPopup';
const EXCLUDED_PATH = /\/(cart|account|password|challenge)(\/|$)/;
const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_GAP_MS = 5000;

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch (error) {
    // Sin almacenamiento disponible: el popup podrá volver a mostrarse en otra visita.
  }
}

class MineraliaNewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.armed = false;
    this.timer = null;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleConsent = this.handleConsent.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSectionSelect = this.handleSectionSelect.bind(this);
    this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('click', this.handleClick);
    this.dialog.addEventListener('cancel', this.handleCancel);
    this.dialog.querySelector('form')?.addEventListener('submit', this.handleSubmit);

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
      return;
    }

    if (EXCLUDED_PATH.test(window.location.pathname)) return;

    const returnedFromForm = window.location.hash === FORM_HASH;
    const success = this.dialog.querySelector('[data-newsletter-success]');
    const error = this.dialog.querySelector('[data-newsletter-error]');

    if (success) {
      // Suscripción completada (aquí o en otro formulario de la página): no se vuelve a ofrecer.
      writeState('subscribed');
      if (returnedFromForm) this.open(success);
      return;
    }

    if (error && returnedFromForm) {
      this.open(this.dialog.querySelector('input[type="email"]'));
      return;
    }

    if (!this.shouldOffer()) return;

    const consentState = document.documentElement.dataset.mineraliaConsent;
    const hasConsentBanner = Boolean(document.querySelector('mineralia-cookie-consent'));

    if (!hasConsentBanner || consentState === 'decided') {
      this.arm();
    } else {
      document.addEventListener(CONSENT_EVENT, this.handleConsent, { once: true });
    }
  }

  disconnectedCallback() {
    this.disarm();
    document.removeEventListener(CONSENT_EVENT, this.handleConsent);
    document.removeEventListener('shopify:section:select', this.handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
  }

  shouldOffer() {
    const saved = readState();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;

    const days = Number(this.dataset.dismissDays) || 30;
    return Date.now() - Number(saved.at || 0) > days * DAY_MS;
  }

  handleConsent() {
    this.arm();
  }

  arm() {
    if (this.armed) return;
    this.armed = true;

    const delay = (Number(this.dataset.delay) || 20) * 1000;
    this.earliest = Date.now() + MIN_GAP_MS;
    this.timer = window.setTimeout(() => this.trigger(), delay);
    document.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
    this.handleScroll();
  }

  disarm() {
    window.clearTimeout(this.timer);
    document.removeEventListener('scroll', this.handleScroll, { capture: true });
    this.armed = false;
  }

  handleScroll(event) {
    // El tema puede desplazar la página dentro de un contenedor; se mide el elemento que hizo scroll.
    const target = event?.target instanceof Element ? event.target : document.scrollingElement;
    if (!target || this.contains(target)) return;
    if (target.clientHeight < window.innerHeight * 0.6) return;

    const scrollable = target.scrollHeight - target.clientHeight;
    if (scrollable <= 0) return;

    const threshold = (Number(this.dataset.scroll) || 50) / 100;
    if (target.scrollTop / scrollable >= threshold) this.trigger();
  }

  trigger() {
    if (this.dialog.open) return;

    const wait = (this.earliest || 0) - Date.now();
    if (wait > 0) {
      this.disarm();
      this.timer = window.setTimeout(() => this.trigger(), wait);
      return;
    }

    // No se encima a otro diálogo abierto (carrito, menú, vista rápida); reintenta más tarde.
    if (document.querySelector('dialog[open]')) {
      this.disarm();
      this.timer = window.setTimeout(() => this.trigger(), 8000);
      return;
    }

    // Tampoco al banner nativo de cookies de Shopify: espera a que el visitante decida.
    const nativeBanner = document.getElementById('shopify-pc__banner');
    if (nativeBanner && nativeBanner.getClientRects().length > 0) {
      this.disarm();
      document.addEventListener('visitorConsentCollected', () => {
        this.earliest = Date.now() + MIN_GAP_MS;
        this.trigger();
      }, { once: true });
      return;
    }

    this.disarm();
    this.open();
  }

  open(focusTarget) {
    if (this.dialog.open) return;
    this.dialog.showModal();

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  dismiss() {
    if (!this.hasAttribute('data-design-mode')) writeState('dismissed');
    this.close();
  }

  handleClick(event) {
    if (event.target.closest('[data-newsletter-close]')) {
      this.dismiss();
      return;
    }

    // Clic en el fondo: el propio <dialog> recibe el evento fuera de su contenido.
    if (event.target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.dismiss();
    }
  }

  handleCancel(event) {
    // Esc cierra y cuenta como descartar.
    event.preventDefault();
    this.dismiss();
  }

  handleSubmit() {
    // Se marca como descartado mientras tanto; si el envío tiene éxito, la página regresa
    // con el mensaje de confirmación y el estado pasa a "subscribed".
    writeState('dismissed');
  }

  handleSectionSelect(event) {
    if (event.detail?.sectionId === this.dataset.sectionId) this.open();
  }

  handleSectionDeselect(event) {
    if (event.detail?.sectionId === this.dataset.sectionId) this.close();
  }
}

if (!customElements.get('mineralia-newsletter-popup')) {
  customElements.define('mineralia-newsletter-popup', MineraliaNewsletterPopup);
}
